import React, { ErrorInfo } from "react"
import {
  getBasename,
  getRoutingMethod,
  getUserDataConfig,
} from "@/utils/helpers/configs"
import Header from "@components/Header/Header"
import MemberView from "@components/MemberView/MemberView"
import { GenericErrorMessage } from "@components/Utils/Utils"
import { Provider } from "react-redux"
import store from "@/store"
import { BrowserRouter, HashRouter } from "react-router-dom"
import { postErrorReport } from "@/utils/helpers/configs"
import { getDeployEnvironment } from "@/utils/helpers/formatters"

export function ApiError(message: string) {
  return new Error(message)
}

interface ErrorContentProps {
  header?: boolean
}

function ErrorContent({ header }: ErrorContentProps) {
  return (
    <>
      {header && <Header />}
      <main className="main" id="main">
        <div className="content-container">
          <GenericErrorMessage />
        </div>
      </main>
    </>
  )
}

interface ErrorBoundaryProps {
  header?: boolean
  children?: React.ReactNode
}

interface ErrorBoundaryState {
  error: boolean | Error
  errorInfo?: ErrorInfo
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { error: false }
  }

  static getDerivedStateFromError(error: Error, errorInfo: ErrorInfo) {
    return { error, errorInfo }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error, errorInfo })
    const payload = {
      memberId: getUserDataConfig()?.memberId,
      environment: getDeployEnvironment(), // NODE_ENV is always production on UAT builds
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack, // shows which component tree crashed
      type: "react-render-error",
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    }
    postErrorReport(payload)
  }

  render() {
    const RouterType =
      getRoutingMethod() === "browser" ? BrowserRouter : HashRouter

    if (this.state.error) {
      const user = getUserDataConfig()
      if (user?.isAdmin) {
        return (
          <Provider store={store}>
            <RouterType basename={getBasename()}>
              <MemberView resetError={() => this.setState({ error: false })}>
                <ErrorContent header={this.props?.header ?? true} />
              </MemberView>
            </RouterType>
          </Provider>
        )
      }

      return (
        <Provider store={store}>
          <RouterType basename={getBasename()}>
            <ErrorContent />
          </RouterType>
        </Provider>
      )
    }
    return this.props.children
  }
}
